import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { FIELD_CONFIG } from 'configs/columns';
import { toPercentage } from 'features/pages/marketplace/BiddingAndPricing/formatting';
import { Link } from 'react-router-dom';
import { marketplaceAssetClassLabel } from 'types/assetClass';

import { DealStatus } from '__generated__/globalTypes';
import { GetSellerListingsData } from './__generated__/index';

type Listing = Extract<
  NonNullable<
    NonNullable<NonNullable<NonNullable<GetSellerListingsData>['user']>['company']>['offerings']
  >[number],
  { __typename: 'UserCompanyListing' }
>;
type Deals = NonNullable<
  NonNullable<
    Extract<
      NonNullable<
        NonNullable<NonNullable<NonNullable<GetSellerListingsData>['user']>['company']>['offerings']
      >[number],
      { __typename: 'UserCompanyListing' }
    >
  >['deals']
>[number];
function isListing(original: Listing | Deals): original is Listing {
  return 'deals' in original;
}

const linkWrapperClassName =
  'text-foreground-default no-underline hover:cursor-pointer hover:text-foreground-muted hover:underline';

const LoanPoolLink = (props: CellContext<Listing | Deals, unknown>) => {
  const original = props.row.original;
  if (isListing(original)) {
    // TODO: Figure out how we want to route to remittance
    // TODO: Need new UI element to handle multiple completed deals per offering
    // For executed trades, we want to navigate to the deal page, not the listing
    // Find the completed deal from the deals array
    const completedDeal = original.deals?.find((deal) => deal.state.status === DealStatus.COMPLETE);
    if (completedDeal) {
      return (
        <Link
          className={linkWrapperClassName}
          to={`../deal/${encodeURIComponent(completedDeal.id)}`}
        >
          {original.providedName}
        </Link>
      );
    }
    // Fallback to listing if no completed deal found
    return (
      <Link className={linkWrapperClassName} to={`../listing/${original.id}`}>
        {original.providedName}
      </Link>
    );
  } else if ('listing' in original) {
    return (
      <Link className={linkWrapperClassName} to={`../deal/${encodeURIComponent(original.id)}`}>
        {original.listing.providedName}
      </Link>
    );
  } else {
    return <></>;
  }
};

export const executedTradesColumnOrder: string[] = [
  'selection',
  'providedName',
  'createdDate',
  'counterparty.name',
  'assetClass',
  'status',
  'loan_count',
  'wa_coupon_bps',
  'wa_loan_age_months',
  'wa_remaining_loan_terms_months',
  'wa_borrower_credit_score',
  'wa_ltv_bps',
];

export const activeColumnDefinitions: ColumnDef<Listing | Deals>[] = [
  {
    accessorFn: (row) => {
      if (isListing(row)) {
        return row.providedName;
      } else {
        return row.listing.providedName;
      }
    },
    header: 'Loan Pool Name',
    id: 'providedName',
    cell: LoanPoolLink,
  },
  {
    accessorKey: 'createdDate',
    id: 'createdDate',
    header: 'Date Created',
    cell: (props) => {
      if (!props.getValue()) {
        return <span>-</span>;
      }
      return <span>{FIELD_CONFIG.maturity_date.display((props.getValue() || '-') as string)}</span>;
    },
  },
  {
    accessorKey: 'counterparty.name',
    id: 'counterparty.name',
    header: 'Counterparty',
    cell: (props) => {
      return props.getValue() || '-';
    },
  },
  {
    header: 'Asset Class',
    id: 'assetClass',
    accessorFn: (row: Listing | Deals) => {
      if ('assetClass' in row) {
        return marketplaceAssetClassLabel(row.assetClass);
      }
      if ('listing' in row && 'assetClass' in row.listing) {
        return marketplaceAssetClassLabel(row.listing.assetClass);
      }
      // Handle other cases or return a default value
      return undefined;
    },
  },
  {
    accessorKey: 'status',
    id: 'status',
    header: 'Completed',
    cell: () => {
      return (
        <span>
          <FontAwesomeIcon icon={faCheckCircle} color="#00ff00" />
        </span>
      );
    },
  },
  {
    accessorKey: 'loan_count',
    id: 'loan_count',
    header: 'Loan Count',
    meta: {
      numeric: true,
    },
    cell: (props) => props.getValue() || '-',
  },
  {
    accessorKey: 'wa_coupon_bps',
    id: 'wa_coupon_bps',
    header: 'GWAC',
    cell: (props) => <span>{toPercentage(Number(props))}</span>,
  },
  {
    accessorKey: 'wa_loan_age_months',
    id: 'wa_loan_age_months',
    header: 'WALA',
    meta: {
      numeric: true,
    },
    cell: (props) => props.getValue() || '-',
  },
  {
    accessorKey: 'wa_remaining_loan_terms_months',
    id: 'wa_remaining_loan_terms_months',
    header: 'WAM',
    meta: {
      numeric: true,
    },
    cell: (props) => props.getValue() || '-',
  },
  {
    accessorKey: 'wa_borrower_credit_score',
    id: 'wa_borrower_credit_score',
    header: 'FICO',
    meta: {
      numeric: true,
    },
    cell: (props) => props.getValue() || '-',
  },
  {
    accessorKey: 'wa_ltv_bps',
    id: 'wa_ltv_bps',
    header: 'LTV',
    cell: (props) => <span>{toPercentage(Number(props))}</span>,
    meta: {
      numeric: true,
    },
  },
];
